import { useEffect } from "react";

import { apiRequest } from "@/api/client";
import { useAuthStore } from "./authStore";
import type { AuthSessionContext, TokenPayload } from "./sessionTypes";

interface RefreshResponse {
  data: TokenPayload;
}

/**
 * Boots the session on mount:
 *   1. If a refresh token is in localStorage, call /v1/auth/refresh.
 *   2. On success, hydrate access token + user.
 *   3. On failure (or no token), mark hydrated with no user.
 */
export function useSessionBootstrap(): { hydrated: boolean } {
  const hydrated = useAuthStore((s) => s.hydrated);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  useEffect(() => {
    if (hydrated) return;
    let cancelled = false;

    (async () => {
      const { setTokens, setSessionContext, markHydrated, clear } = useAuthStore.getState();

      if (!refreshToken) {
        markHydrated();
        return;
      }
      try {
        const json = await apiRequest<RefreshResponse>("/v1/auth/refresh", {
          method: "POST",
          body: { refresh_token: refreshToken },
          anonymous: true,
        });
        if (cancelled) return;
        setTokens({
          accessToken: json.data.access_token,
          refreshToken: json.data.refresh_token,
        });
        setSessionContext(buildSession(json.data));
      } catch {
        if (cancelled) return;
        clear();
      } finally {
        if (!cancelled) markHydrated();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrated, refreshToken]);

  return { hydrated };
}

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}
